/**
 * History's cumulative P&L chart (docs/history-ux.md §2): the running total as a line on top, each
 * day's net as a small green/red bar in a strip underneath, sharing one date axis. Inline SVG drawn
 * at the container's real pixel width (so text never scales), a muted grid, a direct label for the
 * running total at the line's end, and an accessible hover / tap / arrow-key readout with the date,
 * that day's net and the running total. The selected calendar day gets an amber band.
 */
import { useEffect, useMemo, useRef, useState, type KeyboardEvent, type PointerEvent } from "react";
import { fmtCompact, fmtSignedAmt, niceTicks, type Book, type DayPoint } from "../../lib/history.js";

const UP = "var(--color-up)";
const DOWN = "var(--color-down)";

function useWidth<T extends HTMLElement>(): [React.RefObject<T>, number] {
  const ref = useRef<T>(null);
  const [w, setW] = useState(0);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    setW(el.clientWidth);
    const ro = new ResizeObserver(([e]) => setW(Math.round(e.contentRect.width)));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return [ref, w];
}

const dayLabel = (day: string, opts: Intl.DateTimeFormatOptions): string =>
  new Date(`${day}T00:00:00Z`).toLocaleDateString(undefined, { timeZone: "UTC", ...opts });

export function PnlChart({
  series,
  ccy,
  selectedDay,
  height = 220,
}: {
  series: DayPoint[];
  ccy: Book;
  selectedDay?: string | null;
  height?: number;
}) {
  const [wrapRef, W] = useWidth<HTMLDivElement>();
  const [cursor, setCursor] = useState<number | null>(null);
  const n = series.length;

  const compact = W > 0 && W < 520;
  const PAD = { l: compact ? 40 : 48, r: compact ? 62 : 76, t: 10, b: 20 };
  const H = height;
  const barH = Math.round(H * 0.22);
  const gap = 10;
  const lineBottom = H - PAD.b - barH - gap;
  const plotW = Math.max(1, W - PAD.l - PAD.r);

  const { ticks, lo, hi, barMax } = useMemo(() => {
    let lo = 0, hi = 0, barMax = 0;
    for (const p of series) {
      lo = Math.min(lo, p.cum);
      hi = Math.max(hi, p.cum);
      barMax = Math.max(barMax, Math.abs(p.net));
    }
    const ticks = niceTicks(lo, hi, 3);
    return { ticks, lo: Math.min(lo, ticks[0]), hi: Math.max(hi, ticks[ticks.length - 1]), barMax };
  }, [series]);

  const x = (i: number) => PAD.l + (n <= 1 ? plotW / 2 : (i / (n - 1)) * plotW);
  const y = (v: number) => PAD.t + (1 - (v - lo) / (hi - lo || 1)) * (lineBottom - PAD.t);
  const barMid = H - PAD.b - barH / 2;
  const barY = (v: number) => (barMax > 0 ? (v / barMax) * (barH / 2) : 0);
  const slot = n > 0 ? plotW / Math.max(1, n - 1) : plotW;
  const barW = Math.max(1, Math.min(14, slot * 0.7));

  const linePath = useMemo(() => {
    if (W === 0 || n === 0) return "";
    return series.map((p, i) => `${i ? "L" : "M"}${x(i).toFixed(1)},${y(p.cum).toFixed(1)}`).join("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [series, W, lo, hi, H]);

  // ~5 date ticks (fewer on phones), never repeating a label.
  const xTicks = useMemo(() => {
    if (n === 0) return [];
    const want = compact ? 3 : 5;
    const idx = n <= want ? series.map((_, i) => i) : Array.from({ length: want }, (_, k) => Math.round((k * (n - 1)) / (want - 1)));
    const long = n > 400;
    return [...new Set(idx)].map((i) => ({
      i,
      label: dayLabel(series[i].day, long ? { month: "short", year: "2-digit" } : { month: "short", day: "numeric" }),
    }));
  }, [series, n, compact]);

  const selIdx = selectedDay ? series.findIndex((p) => p.day === selectedDay) : -1;
  const last = n > 0 ? series[n - 1] : null;
  const hover = cursor != null && cursor >= 0 && cursor < n ? series[cursor] : null;

  const pick = (e: PointerEvent<SVGSVGElement>) => {
    const r = e.currentTarget.getBoundingClientRect();
    const fx = e.clientX - r.left;
    const i = n <= 1 ? 0 : Math.round(((fx - PAD.l) / plotW) * (n - 1));
    setCursor(Math.max(0, Math.min(n - 1, i)));
  };
  const onKey = (e: KeyboardEvent<HTMLDivElement>) => {
    if (n === 0) return;
    const step = e.shiftKey ? 7 : 1;
    if (e.key === "ArrowLeft") setCursor((c) => Math.max(0, (c ?? n) - step));
    else if (e.key === "ArrowRight") setCursor((c) => Math.min(n - 1, (c ?? -1) + step));
    else if (e.key === "Home") setCursor(0);
    else if (e.key === "End") setCursor(n - 1);
    else if (e.key === "Escape") setCursor(null);
    else return;
    e.preventDefault();
  };

  const summary = last
    ? `Cumulative P&L from ${dayLabel(series[0].day, { month: "short", day: "numeric", year: "numeric" })} to ${dayLabel(last.day, {
        month: "short",
        day: "numeric",
        year: "numeric",
      })}: ${fmtSignedAmt(last.cum, ccy)}. Use the arrow keys to read each day.`
    : "No days to chart.";

  const tipLeft = hover && cursor != null ? Math.min(Math.max(x(cursor), 90), Math.max(90, W - 90)) : 0;

  return (
    <div
      ref={wrapRef}
      className="relative select-none rounded-md outline-none focus-visible:ring-2 focus-visible:ring-amber/60"
      style={{ height: H }}
      tabIndex={n > 0 ? 0 : -1}
      role="group"
      aria-label={summary}
      onKeyDown={onKey}
      onBlur={() => setCursor(null)}
    >
      {W > 0 && (
        <svg
          width={W}
          height={H}
          className="block touch-pan-y"
          onPointerMove={pick}
          onPointerDown={pick}
          onPointerLeave={(e) => e.pointerType === "mouse" && setCursor(null)}
          aria-hidden
        >
          {/* selected calendar day */}
          {selIdx >= 0 && (
            <rect
              x={x(selIdx) - Math.max(3, barW / 2 + 2)}
              y={PAD.t}
              width={Math.max(6, barW + 4)}
              height={H - PAD.t - PAD.b}
              rx={3}
              fill="var(--color-amber)"
              fillOpacity={0.14}
            />
          )}
          {/* recessive grid + money labels for the running total */}
          {ticks.map((t) => (
            <g key={t}>
              <line
                x1={PAD.l}
                x2={W - PAD.r}
                y1={y(t)}
                y2={y(t)}
                stroke="currentColor"
                className="text-ink"
                strokeOpacity={t === 0 ? 0.22 : 0.07}
                strokeDasharray={t === 0 ? undefined : "2 4"}
              />
              <text x={PAD.l - 6} y={y(t) + 3.5} textAnchor="end" fontSize={10} className="nums fill-faint">
                {fmtCompact(t, ccy)}
              </text>
            </g>
          ))}
          {/* the daily-net strip: zero line + bars */}
          <line x1={PAD.l} x2={W - PAD.r} y1={barMid} y2={barMid} stroke="currentColor" className="text-ink" strokeOpacity={0.12} />
          <text x={PAD.l - 6} y={barMid + 3.5} textAnchor="end" fontSize={10} className="fill-faint">
            day
          </text>
          {series.map((p, i) =>
            Math.abs(p.net) < 0.005 ? null : (
              <rect
                key={p.day}
                x={x(i) - barW / 2}
                y={p.net > 0 ? barMid - barY(p.net) : barMid}
                width={barW}
                height={Math.max(1, Math.abs(barY(p.net)))}
                rx={Math.min(2, barW / 3)}
                fill={p.net > 0 ? UP : DOWN}
                fillOpacity={cursor === i || selIdx === i ? 0.95 : 0.6}
              />
            ),
          )}
          {/* the running total */}
          <path d={linePath} fill="none" stroke="var(--color-ink)" strokeWidth={1.75} strokeLinejoin="round" strokeLinecap="round" />
          {last && (
            <>
              <circle cx={x(n - 1)} cy={y(last.cum)} r={3} fill="var(--color-ink)" />
              <text
                x={x(n - 1) + 7}
                y={y(last.cum) + 4}
                fontSize={compact ? 11 : 12}
                fontWeight={600}
                className="nums"
                fill={last.cum >= 0.005 ? UP : last.cum <= -0.005 ? DOWN : "var(--color-dim)"}
              >
                {fmtCompact(last.cum, ccy)}
              </text>
            </>
          )}
          {/* date axis */}
          {xTicks.map(({ i, label }, k) => (
            <text
              key={i}
              x={x(i)}
              y={H - 5}
              fontSize={10}
              textAnchor={xTicks.length > 1 && k === 0 ? "start" : k === xTicks.length - 1 && xTicks.length > 1 ? "end" : "middle"}
              className="nums fill-faint"
            >
              {label}
            </text>
          ))}
          {/* hover */}
          {hover && cursor != null && (
            <g pointerEvents="none">
              <line x1={x(cursor)} x2={x(cursor)} y1={PAD.t} y2={H - PAD.b} stroke="currentColor" className="text-ink" strokeOpacity={0.3} />
              <circle cx={x(cursor)} cy={y(hover.cum)} r={4} fill="var(--color-bg)" stroke="var(--color-ink)" strokeWidth={2} />
            </g>
          )}
        </svg>
      )}
      {/* readout: visible tooltip + the same text for screen readers */}
      <div aria-live="polite" className="sr-only">
        {hover ? `${dayLabel(hover.day, { weekday: "short", month: "short", day: "numeric" })}: day ${fmtSignedAmt(hover.net, ccy)}, running total ${fmtSignedAmt(hover.cum, ccy)}` : ""}
      </div>
      {hover && (
        <div
          aria-hidden
          className="pointer-events-none absolute top-1 z-10 w-[168px] -translate-x-1/2 rounded-md border border-border-2 bg-panel px-2.5 py-2 text-[12px] shadow-lg"
          style={{ left: tipLeft }}
        >
          <div className="text-[11px] text-dim">{dayLabel(hover.day, { weekday: "short", month: "short", day: "numeric", year: "numeric" })}</div>
          <div className="mt-1 flex justify-between gap-2">
            <span className="text-faint">Day</span>
            <span className={`nums ${tone(hover.net)}`}>{fmtSignedAmt(hover.net, ccy)}</span>
          </div>
          <div className="flex justify-between gap-2">
            <span className="text-faint">Running</span>
            <span className={`nums font-semibold ${tone(hover.cum)}`}>{fmtSignedAmt(hover.cum, ccy)}</span>
          </div>
          {hover.active && (
            <div className="nums mt-0.5 text-[11px] text-faint">
              {hover.wins}W · {hover.losses}L{hover.pushes ? ` · ${hover.pushes}P` : ""}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const tone = (n: number): string => (n >= 0.005 ? "text-up" : n <= -0.005 ? "text-down" : "text-dim");
